import type { TFunction } from 'i18next';

import {
  BeneficialOwnerType,
  BusinessType,
  ControlType,
  EntityClassification,
  ExcludedPersonType,
  FinancialOrganisationType,
  LegalForm,
  RegulatedMarketListing,
} from '@/constants/institution';
import type { InstitutionDetailsV2Company, InstitutionDetailsV2OnboardingKey, InstitutionDetailsV2Partial } from '@/services/api/profile/institution-details-v2';

type InstitutionReviewSectionSummaryOptions = {
  t: TFunction;
  getCountryName: (country: string) => string;
};

const SEGMENT_SEPARATOR = ' · ';

const getFirstWithMore = (values: string[], t: TFunction) => {
  const [first, ...rest] = values;

  return rest.length ? t('institutionDetails.review.summary.more', { value: first, count: rest.length }) : first;
};

const getTypeOfBusinessSegment = ({ typeOfBusiness, subTypeOfBusiness, typeOfBusinessOther }: InstitutionDetailsV2Company, t: TFunction) => {
  const typeOfBusinessLabel = t(`institutionDetails.company.typeOfBusiness.options.${typeOfBusiness}`);

  if (typeOfBusiness === BusinessType.OTHER) return typeOfBusinessOther;

  if (!subTypeOfBusiness) return typeOfBusinessLabel;

  return t('institutionDetails.review.summary.company.subTypeOfBusiness', {
    typeOfBusiness: typeOfBusinessLabel,
    subTypeOfBusiness: t(`institutionDetails.company.subTypeOfBusiness.options.${subTypeOfBusiness}`),
  });
};

// Follow-up free text is part of its question, and uploads are counted as documents
const getAnsweredQuestionsSegments = (section: object, t: TFunction) => {
  const answeredKeys = Object.entries(section).flatMap(([key, value]) => (value === undefined || value === null || key.endsWith('Other') ? [] : [key]));
  const documents = answeredKeys.filter((key) => key.endsWith('Document')).length;

  return [
    t('institutionDetails.review.summary.answers', { count: answeredKeys.length - documents }),
    documents ? t('institutionDetails.review.summary.documents', { count: documents }) : undefined,
  ];
};

const getInstitutionReviewSectionSegments = (
  key: InstitutionDetailsV2OnboardingKey,
  answers: InstitutionDetailsV2Partial,
  { t, getCountryName }: InstitutionReviewSectionSummaryOptions,
): Array<string | undefined> => {
  const {
    incorporation,
    branchOperations,
    company,
    businessProfile,
    ownership,
    regulatoryInformation,
    amlCtfSanctions,
    complianceTraining,
    kycCdd,
    expectedActivity,
    taxSelfCertification,
    products,
    decisionMaker,
    execution,
    clientCategorisation,
    reporting,
  } = answers;

  switch (key) {
    case 'incorporation':
      return incorporation ? [getCountryName(incorporation.countryOfIncorporation)] : [];

    case 'branchOperations':
      if (!branchOperations) return [];

      return [
        branchOperations.differentJurisdiction === 'YES'
          ? t('institutionDetails.review.summary.branchOperations.YES', { country: getCountryName(branchOperations.countryOfBranchOperations) })
          : t('institutionDetails.review.summary.branchOperations.NO'),
      ];

    case 'company':
      if (!company) return [];

      return [
        company.companyName,
        company.legalForm === LegalForm.OTHER ? company.legalFormOther : t(`institutionDetails.company.legalForm.options.${company.legalForm}`),
        getTypeOfBusinessSegment(company, t),
        company.leiNumber ? t('institutionDetails.review.summary.company.leiNumber', { leiNumber: company.leiNumber }) : undefined,
      ];

    case 'businessProfile':
      if (!businessProfile) return [];

      return [
        t(`institutionDetails.review.summary.businessProfile.fundsOwnership.${businessProfile.fundsOwnership}`),
        t('institutionDetails.review.summary.businessProfile.annualRevenue', { band: t(`institutionDetails.businessProfile.businessSizeBands.${businessProfile.annualRevenue}`) }),
        t('institutionDetails.review.summary.businessProfile.employees', { band: t(`institutionDetails.businessProfile.employeeBands.${businessProfile.employees}`) }),
      ];

    case 'ownership': {
      if (!ownership) return [];

      const management = t('institutionDetails.review.summary.ownership.management', { count: ownership.management.length });

      if (ownership.regulatedMarketListing === RegulatedMarketListing.LISTED) {
        return [t('institutionDetails.review.summary.ownership.listed', { identifier: ownership.listedCompany.ticker ?? ownership.listedCompany.isin }), management];
      }

      if (ownership.regulatedMarketListing === RegulatedMarketListing.WHOLLY_OWNED_SUBSIDIARY) {
        return [t('institutionDetails.review.summary.ownership.whollyOwnedSubsidiary', { name: ownership.listedCompany.name }), management];
      }

      const owners = ownership.ubos.filter((ubo) => ubo.type === BeneficialOwnerType.OWNER);

      return [
        t(`institutionDetails.ownership.type.options.${ownership.ownershipType}`),
        owners.length ? t('institutionDetails.review.summary.ownership.ubos', { count: owners.length }) : t('institutionDetails.review.summary.ownership.seniorManagingOfficial'),
        management,
        ownership.hasCorporateOwners === 'YES' ? t('institutionDetails.review.summary.ownership.corporateOwners', { count: ownership.corporateOwners.length }) : undefined,
      ];
    }

    case 'regulatoryInformation':
      if (!regulatoryInformation) return [];

      return [
        regulatoryInformation.hasRegulatorySupervisionOrOversight === 'YES'
          ? t('institutionDetails.review.summary.regulatoryInformation.supervised')
          : t('institutionDetails.review.summary.regulatoryInformation.notSupervised'),
        regulatoryInformation.hasRegulatorySupervisionOrOversight === 'YES' && regulatoryInformation.isMifidInvestmentFirm === 'YES'
          ? t('institutionDetails.review.summary.regulatoryInformation.mifidInvestmentFirm')
          : undefined,
        regulatoryInformation.offshoreLicence === 'YES'
          ? t('institutionDetails.review.summary.regulatoryInformation.offshoreLicence')
          : t('institutionDetails.review.summary.regulatoryInformation.noOffshoreLicence'),
      ];

    case 'amlCtfSanctions':
      return amlCtfSanctions ? getAnsweredQuestionsSegments(amlCtfSanctions, t) : [];

    case 'complianceTraining':
      return complianceTraining ? getAnsweredQuestionsSegments(complianceTraining, t) : [];

    case 'kycCdd':
      return kycCdd ? getAnsweredQuestionsSegments(kycCdd, t) : [];

    case 'expectedActivity':
      if (!expectedActivity) return [];

      return [
        getFirstWithMore(
          expectedActivity.accountPurpose.map((purpose) => t(`institutionDetails.expectedActivity.accountPurposeOptions.${purpose}`)),
          t,
        ),
        t('institutionDetails.review.summary.expectedActivity.monthlyTradingVolume', {
          band: t(`institutionDetails.expectedActivity.tradingVolumeBands.${expectedActivity.monthlyTradingVolume}`),
        }),
      ];

    case 'taxSelfCertification': {
      if (!taxSelfCertification) return [];

      const residencySegments =
        taxSelfCertification.noTaxResidence === 'YES'
          ? [
              t('institutionDetails.review.summary.taxSelfCertification.noTaxResidence'),
              t('institutionDetails.review.summary.taxSelfCertification.placeOfEffectiveManagement', {
                country: getCountryName(taxSelfCertification.placeOfEffectiveManagement),
              }),
            ]
          : [
              getFirstWithMore(
                taxSelfCertification.taxResidencies.map(({ jurisdiction }) => getCountryName(jurisdiction)),
                t,
              ),
            ];

      if (taxSelfCertification.excludedPersonType !== ExcludedPersonType.NONE) {
        return [...residencySegments, t(`institutionDetails.review.summary.taxSelfCertification.excludedPersonType.${taxSelfCertification.excludedPersonType}`)];
      }

      return [
        ...residencySegments,
        t(`institutionDetails.review.summary.taxSelfCertification.entityClassification.${taxSelfCertification.entityClassification}`),
        taxSelfCertification.entityClassification === EntityClassification.PASSIVE_ENTITY
          ? t('institutionDetails.review.summary.taxSelfCertification.controllingPersons', { count: taxSelfCertification.controllingPersons.length })
          : undefined,
      ];
    }

    case 'products':
      return products ? [t(`institutionDetails.review.summary.products.${products.futures}`)] : [];

    case 'decisionMaker':
      if (!decisionMaker) return [];

      return decisionMaker.decisionMaker === ControlType.ALGORITHM
        ? [t('institutionDetails.controlType.options.ALGORITHM'), decisionMaker.algorithm.algorithmId]
        : [
            getFirstWithMore(
              decisionMaker.individuals.map(({ firstName, lastName }) => `${firstName} ${lastName}`),
              t,
            ),
          ];

    case 'execution':
      if (!execution) return [];

      return execution.execution === ControlType.ALGORITHM
        ? [t('institutionDetails.controlType.options.ALGORITHM'), execution.algorithm.algorithmId]
        : [
            getFirstWithMore(
              execution.individuals.map(({ firstName, lastName }) => `${firstName} ${lastName}`),
              t,
            ),
          ];

    case 'clientCategorisation':
      if (!clientCategorisation) return [];

      return [
        t(`institutionDetails.review.summary.clientCategorisation.organisationType.${clientCategorisation.organisationType}`),
        'financialOrganisationType' in clientCategorisation && clientCategorisation.financialOrganisationType !== FinancialOrganisationType.NONE_OF_THE_ABOVE
          ? t(`institutionDetails.review.summary.clientCategorisation.financialOrganisationType.${clientCategorisation.financialOrganisationType}`)
          : undefined,
      ];

    case 'reporting':
      if (!reporting) return [];

      return [
        t('institutionDetails.review.summary.reporting.mifid', { answer: reporting.mifid === 'YES' ? t('fundamentals.yes') : t('fundamentals.no') }),
        t('institutionDetails.review.summary.reporting.emir', { answer: reporting.emir === 'YES' ? t('fundamentals.yes') : t('fundamentals.no') }),
        t(`institutionDetails.review.summary.reporting.emirClassification.${reporting.emirClassification}`),
      ];

    case 'authorisation':
      return [];
  }
};

export const getInstitutionReviewSectionSummary = (key: InstitutionDetailsV2OnboardingKey, answers: InstitutionDetailsV2Partial, options: InstitutionReviewSectionSummaryOptions) =>
  getInstitutionReviewSectionSegments(key, answers, options)
    .filter((segment) => !!segment)
    .join(SEGMENT_SEPARATOR);
